import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Checkbox,
  Flex,
  Text,
  Input,
  Select,
  HStack,
  VStack,
  useColorModeValue,
  Spinner,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  SearchIcon, 
  ArrowBackIcon,
  DownloadIcon,
  RepeatIcon,
  ViewIcon,
  ExternalLinkIcon,
  CheckIcon
} from '@chakra-ui/icons';
import { getToken } from '../../../../../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../../context/AuthContext';
import PrecatoriosSelecionadasModal from './PrecatoriosSelecionadasModal';
import useHandleOpenDetails from './useHandleOpenDetails';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

const PrecatoriosSelecionadasRJ = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [filteredPrecatorios, setFilteredPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('numero_precatorio');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('ordem');
  const [sortOrder, setSortOrder] = useState('asc');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useAuth();
  
  const toast = useToast();
  const navigate = useNavigate();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const theadBg = useColorModeValue('gray.50', 'gray.900');

  const { selectedPrecatorio, detailsLoading, handleOpenDetails } = useHandleOpenDetails();

  useEffect(() => {
    fetchPrecatoriosSelecionados();
  }, []);

  useEffect(() => {
    filterAndSortPrecatorios();
  }, [precatorios, searchTerm, filterField, statusFilter, sortField, sortOrder]);

  const fetchPrecatoriosSelecionados = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRJ/selecionados`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios selecionados');
      }

      const data = await response.json();
      setPrecatorios(data.data || []);
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPrecatorios = () => {
    let filtered = [...precatorios];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const value = item[filterField]?.toString().toLowerCase() || '';
        return value.includes(searchTerm.toLowerCase());
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (sortField === 'valor_atualizado' || sortField === 'ordem') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPrecatorios(filtered);
  };

  const handleStatusUpdate = async (precatorioId, newStatus) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRJ/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          precatorioId,
          status: newStatus,
          userId: user?._id
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status');
      }

      const result = await response.json();
      
      toast({
        title: 'Status atualizado',
        description: `Status alterado para ${newStatus}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the list
      fetchPrecatoriosSelecionados();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Nenhum item selecionado',
        description: 'Selecione pelo menos um precatório para atualizar o status.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRJ/update-status-bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          precatorioIds: Array.from(selectedItems),
          status: newStatus,
          userId: user?._id
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status em massa');
      }

      const result = await response.json();
      
      toast({
        title: 'Status atualizado',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setSelectedItems(new Set());
      fetchPrecatoriosSelecionados();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDesselecionar = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Nenhum item selecionado',
        description: 'Selecione pelo menos um precatório para desmarcar.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRJ/desselecionar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectedIds: Array.from(selectedItems)
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao desmarcar precatórios');
      }

      const result = await response.json();
      
      toast({
        title: 'Sucesso!',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Remove deselected items from the list
      setPrecatorios(prev => prev.filter(p => !selectedItems.has(p._id)));
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao desmarcar precatórios',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(new Set(filteredPrecatorios.map(p => p._id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Não Contatou': 'gray',
      'Pendente': 'yellow',
      'Em Análise': 'blue',
      'Proposta Enviada': 'purple',
      'Proposta Aceita': 'green',
      'Contrato Assinado': 'teal',
      'Pago': 'green',
      'Arquivado': 'red'
    };
    return colors[status] || 'gray';
  };

  const statusOptions = [
    'Não Contatou',
    'Pendente',
    'Em Análise',
    'Proposta Enviada',
    'Proposta Aceita',
    'Contrato Assinado',
    'Pago',
    'Arquivado'
  ];

  // Calculate statistics
  const stats = {
    total: precatorios.length,
    totalValue: precatorios.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0),
    byStatus: statusOptions.reduce((acc, status) => {
      const items = precatorios.filter(p => p.status === status);
      acc[status] = {
        count: items.length,
        value: items.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0)
      };
      return acc;
    }, {})
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <IconButton
              icon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/precatorios/rj')}
              variant="ghost"
              aria-label="Voltar"
              mr={4}
            />
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                Precatórios Selecionados - RJ
              </Text>
              <Text color={textColor} mt={1}>
                {stats.total} precatórios no valor total de {formatCurrency(stats.totalValue)}
              </Text>
            </Box>
          </Flex>
          <HStack spacing={4}>
            <Button
              leftIcon={<RepeatIcon />}
              onClick={fetchPrecatoriosSelecionados}
              variant="outline"
            >
              Atualizar
            </Button>
            <Button
              leftIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/admin/precatorios/rj/sincronizar')}
              colorScheme="purple"
            >
              Sincronizar JurisConsult
            </Button>
          </HStack>
        </Flex>

        {/* Statistics */}
        <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
          {statusOptions.map(status => (
            <Box
              key={status}
              bg={bg}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Badge colorScheme={getStatusColor(status)} mb={2}>
                {status}
              </Badge>
              <Text fontSize="xl" fontWeight="bold" color={headingColor}>
                {stats.byStatus[status].count}
              </Text>
              <Text fontSize="sm" color={textColor}>
                {formatCurrency(stats.byStatus[status].value)}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* Actions Bar */}
        <Box bg={sectionBg} p={4} borderRadius="lg">
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <Icon as={SearchIcon} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                maxW="200px"
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
              >
                <option value="numero_precatorio">Número</option>
                <option value="ente">Ente</option>
                <option value="beneficiario">Beneficiário</option>
                <option value="natureza">Natureza</option>
              </Select>

              <Select
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Select>

              <Button
                colorScheme="blue"
                onClick={() => navigate('/admin/precatorios/rj/alto-valor')}
              >
                Alto Valor
              </Button>

              <Button
                colorScheme="orange"
                onClick={() => navigate('/admin/precatorios/rj/pendentes')}
              >
                Pendentes
              </Button>
            </HStack>

            {selectedItems.size > 0 && (
              <HStack spacing={4} p={4} bg={selectedBg} borderRadius="md">
                <Text fontWeight="medium">
                  {selectedItems.size} selecionados
                </Text>
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm">
                    Atualizar Status
                  </MenuButton>
                  <MenuList>
                    {statusOptions.map(status => (
                      <MenuItem key={status} onClick={() => handleBulkStatusUpdate(status)}>
                        <Badge colorScheme={getStatusColor(status)} mr={2}>
                          {status}
                        </Badge>
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDesselecionar}
                >
                  Desmarcar Selecionados
                </Button>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Table */}
        <Box bg={bg} borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={theadBg}>
                <Tr>
                  <Th width="50px">
                    <Checkbox
                      isChecked={
                        filteredPrecatorios.length > 0 &&
                        filteredPrecatorios.every(item => selectedItems.has(item._id))
                      }
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th>Ações</Th>
                  <Th>Ordem</Th>
                  <Th>Número</Th>
                  <Th>Status</Th>
                  <Th>Ente</Th>
                  <Th>Beneficiário</Th>
                  <Th>Natureza</Th>
                  <Th>Orçamento</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredPrecatorios.map((precatorio) => (
                  <Tr 
                    key={precatorio._id}
                    _hover={{ bg: hoverBg }}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedItems.has(precatorio._id)}
                        onChange={() => handleSelectItem(precatorio._id)}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="Ver Detalhes">
                          <IconButton
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                            aria-label="Ver detalhes"
                            onClick={() => handleOpenDetails(precatorio, onOpen)}
                          />
                        </Tooltip>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            size="sm"
                            variant="ghost"
                            aria-label="Menu"
                          />
                          <MenuList>
                            {statusOptions.map(status => (
                              <MenuItem
                                key={status}
                                onClick={() => handleStatusUpdate(precatorio._id, status)}
                              >
                                <Badge colorScheme={getStatusColor(status)} mr={2}>
                                  {status}
                                </Badge>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Td>
                    <Td>{precatorio.ordem}</Td>
                    <Td fontWeight="medium">{precatorio.numero_precatorio}</Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(precatorio.status)}>
                        {precatorio.status}
                      </Badge>
                    </Td>
                    <Td>{precatorio.ente}</Td>
                    <Td>{precatorio.beneficiario}</Td>
                    <Td>{precatorio.natureza}</Td>
                    <Td>{precatorio.orcamento}</Td>
                    <Td isNumeric fontWeight="medium">
                      {formatCurrency(precatorio.valor_atualizado)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </VStack>

      {/* Details Modal */}
      <PrecatoriosSelecionadasModal
        isOpen={isOpen}
        onClose={onClose}
        precatorio={selectedPrecatorio}
        loading={detailsLoading}
      />
    </Box>
  );
};

export default PrecatoriosSelecionadasRJ;